import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { AgGridAngular } from "ag-grid-angular";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";

import {
    ClientSideRowModelModule,
    ModuleRegistry,
    TextFilterModule,
    NumberFilterModule,
    GridApi,
    ColDef,
    TextEditorModule,
    LargeTextEditorModule,
    CellEditingStartedEvent,
    CellEditingStoppedEvent,
    RowStyleModule,
    RenderApiModule,
    ClientSideRowModelApiModule,
    NewValueParams,
    ScrollApiModule,
    HighlightChangesModule,
    TooltipModule,
} from "ag-grid-community";

import {
    NgbModal,
    NgbModalOptions,
} from "@ng-bootstrap/ng-bootstrap";

import { ItemNumberApiService } from "src/app/core/api/item-number-api.service";
import { ContractApiService } from "src/app/core/api/contract-api.service";
import { ExtensionApiService } from "src/app/core/api/extension-api.service";
import { ContractDetailsApiService } from "src/app/core/api/contract-details-api.service";
import { ItemNumber } from "src/app/core/models/item-number";
import { ToastrService } from "ngx-toastr";
import { ActionsComponent } from "./components/actions/actions.component";
import { CreateItemNumberModalComponent } from "./components/create-item-number-modal/create-item-number-modal.component";
import { take } from "rxjs";

ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    TextFilterModule,
    NumberFilterModule,
    TextEditorModule,
    LargeTextEditorModule,
    RowStyleModule,
    RenderApiModule,
    ClientSideRowModelApiModule,
    ScrollApiModule,
    HighlightChangesModule,
    TooltipModule,
]);

@Component({
    selector: "app-item-numbers",
    standalone: true,
    imports: [AgGridAngular, TranslatePipe, FormsModule, ReactiveFormsModule],
    templateUrl: "./item-numbers.component.html",
    styleUrl: "./item-numbers.component.scss",
})
export class ItemNumbersComponent implements OnInit {
    public isCellEditing = false;
    getRowStyle: any;
    gridApi: GridApi<ItemNumber>;

    public contractId: number;
    public ownerType: "contract" | "extension";
    public ownerId: number;
    public contractDetailsId: number;

    public taskTitle = "";
    public ownerLabel = "";
    public contractDetailsLabel = "";

    public itemNumberList: ItemNumber[];
    public gridTitles: any = {};
    public colDefs: ColDef<ItemNumber>[] = [];

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private modalService: NgbModal,
        private itemNumberApiService: ItemNumberApiService,
        private contractApiService: ContractApiService,
        private extensionApiService: ExtensionApiService,
        private contractDetailsApiService: ContractDetailsApiService,
        private translateService: TranslateService,
        private toastr: ToastrService
    ) {}

    public validationOnEmptyCell(event: NewValueParams<ItemNumber, any, any>) {
        event.data[event.colDef.field as any] = event.oldValue;
        this.gridApi.refreshCells({ force: true });

        this.gridApi.startEditingCell({
            rowIndex: event.node.rowIndex,
            colKey: event.colDef.field,
        });
    }

    public updateField(eventData: NewValueParams<ItemNumber, any, any>) {
        this.itemNumberApiService
            .patch(eventData.data)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    this.translateService
                        .get("itemNumbers.form.update", { field: eventData.colDef.headerName })
                        .subscribe((res) => this.toastr.success(res));
                },
                error: () => {
                    this.translateService
                        .get("itemNumbers.form.updateError", { field: eventData.colDef.headerName })
                        .subscribe((res) => this.toastr.error(res));
                },
            });
    }

    defaultColDef = {
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: true,
        suppressFloatingFilterButton: true,
        suppressHeaderFilterButton: true,
    };

    gridContext = {
        parentComponent: this,
    };

    private initializeColumnDefs() {
        this.colDefs = [
            {
                field: "title",
                headerName: this.gridTitles.title,
                filter: "agTextColumnFilter",
                editable: true,
                flex: 1,
                onCellValueChanged: (event) => {
                    if (event.newValue === null) {
                        this.toastr.error(this.translateService.instant("itemNumbers.form.emptyCell"));
                        this.validationOnEmptyCell(event);
                        return;
                    }
                    this.updateField(event);
                },
            },
            {
                field: "description",
                headerName: this.gridTitles.description,
                filter: "agTextColumnFilter",
                editable: true,
                flex: 2,
                cellEditor: "agLargeTextCellEditor",
                onCellValueChanged: (event) => {
                    this.updateField(event);
                },
            },
            {
                field: "hours",
                headerName: this.gridTitles.hours,
                editable: true,
                width: 130,
                onCellValueChanged: (event) => {
                    this.updateField(event);
                },
            },
            {
                headerName: this.gridTitles.actions ?? "Actions",
                cellRenderer: ActionsComponent,
                cellRendererParams: {
                    context: this.gridContext,
                },
                filter: false,
                width: 120,
                pinned: "right",
                resizable: false,
                lockPinned: true,
                lockPosition: true,
            },
        ];
    }

    ngOnInit(): void {
        this.contractId = Number(this.route.snapshot.paramMap.get("contractId"));
        this.ownerType = this.route.snapshot.paramMap.get("ownerType") as "contract" | "extension";
        this.ownerId = Number(this.route.snapshot.paramMap.get("ownerId"));
        this.contractDetailsId = Number(this.route.snapshot.paramMap.get("contractDetailsId"));

        this.translateService.stream("itemNumbers.form").subscribe((res) => {
            this.gridTitles = res;
            this.initializeColumnDefs();
        });
        this.loadItemNumberList();
        this.loadHeaderInfo();
    }

    private loadHeaderInfo() {
        this.contractApiService
            .getContractByContractId(this.contractId)
            .subscribe((contract) => {
                this.taskTitle = contract?.taskSmall?.title ?? "";
            });

        if (this.ownerType === "extension") {
            this.extensionApiService
                .getByContract(this.contractId)
                .subscribe((extensions) => {
                    const index = extensions.findIndex(
                        (e) => e.id === this.ownerId
                    );
                    const number = index >= 0 ? index + 1 : this.ownerId;
                    this.ownerLabel = this.translateService.instant(
                        "extensions.extensionLabel",
                        { number }
                    );
                });
        } else {
            this.ownerLabel = this.translateService.instant(
                "extensions.contractLabel",
                { number: 1 }
            );
        }

        const owner =
            this.ownerType === "extension"
                ? { extensionId: this.ownerId }
                : { contractId: this.ownerId };
        this.contractDetailsApiService.getByOwner(owner).subscribe((list) => {
            const index = list.findIndex((d) => d.id === this.contractDetailsId);
            const number = index >= 0 ? index + 1 : this.contractDetailsId;
            this.contractDetailsLabel = this.translateService.instant(
                "contractDetails.label",
                { number }
            );
        });
    }

    public goToExtensions() {
        this.router.navigate(["contract", this.contractId, "details"]);
    }

    public goToContractDetails() {
        this.router.navigate([
            "contract",
            this.contractId,
            "details",
            this.ownerType,
            this.ownerId,
            "contract-details",
        ]);
    }

    private loadItemNumberList() {
        this.itemNumberApiService.getByContractDetails(this.contractDetailsId).subscribe({
            next: (result) => {
                this.itemNumberList = result;
                if (this.gridApi) {
                    this.gridApi.setGridOption("rowData", this.itemNumberList);
                }
            }
        });
    }

    onGridReady(params: any) {
        this.gridApi = params.api;
        this.gridApi.setGridOption("rowData", this.itemNumberList);
    }

    onCellEditingStarted(event: CellEditingStartedEvent) {
        this.isCellEditing = true;
        this.getRowStyle = (params: any) => {
            if (params.node.id === event.node.id) {
                return { background: "#b0d7efc9" };
            }
            return null;
        };
    }

    onCellEditingStopped(event: CellEditingStoppedEvent) {
        this.isCellEditing = false;
        this.getRowStyle = (params: any) => {
            if (params.node.id === event.node.id) {
                return { background: "" };
            }
            return null;
        };
    }

    public goBack() {
        this.router.navigate([
            "contract",
            this.contractId,
            "details",
            this.ownerType,
            this.ownerId,
            "contract-details",
        ]);
    }

    public openModal() {
        const options: NgbModalOptions = {
            centered: true,
            backdrop: true,
            size: "lg",
        };

        const modalRef = this.modalService.open(CreateItemNumberModalComponent, options);
        modalRef.componentInstance.contractDetailsId = Number(this.contractDetailsId);

        modalRef.result.then(
            (result) => {
                this.gridApi.applyTransaction({ add: [result] });
            },
            () => {}
        );
    }
}
